import React, {PropsWithChildren, useCallback, useMemo} from "react";
import {
  Entity,
  OtsState,
  permissionABACPolicyAssocsSelector,
  permissionsNameSelector,
  Person, PersonEntity,
  transformPerson,
  useGetUserPermissionABACPolicyAssocsQuery,
  useGetUserPermissionsQuery
} from "@ots-frontend/ots-data";
import {Selector} from "@reduxjs/toolkit";
import {useSelector} from "react-redux";
import {skipToken} from "@reduxjs/toolkit/query";
import {ABACContext, ABACContextSubject, hasAccessHelper} from "./HasAccess";

export type AuthContextType = {
  authenticated: boolean
  /**
   * Signed in through single sign-on, so the password belongs to that sign-in rather than to this
   * account and cannot be changed here.
   */
  ssoAuthenticated: boolean
  user?: PersonEntity
  userId?: string;
  companyId?: string
  permissions: string[]
  hasAccess: (
    objOrPerm?: Record<string, any> | string,
    ...perms: string[]) => boolean,
  hasEntityAccess: (entity: Entity, ...perms: string[]) => boolean,
};

export const AuthContext = React.createContext<AuthContextType>(undefined!);
export const useAuth = () => React.useContext(AuthContext);

export type AuthProviderProps<S extends OtsState> = {
  getAuthToken: Selector<S, string | undefined>
  getAuthUser: Selector<S, Person | undefined>
  generatePermissions: (userType: string | undefined) => string[]
  /** Whether this session came through single sign-on. Defaults to false. */
  ssoAuthenticated?: boolean
}

export function AuthProvider<S extends OtsState>(props: PropsWithChildren<AuthProviderProps<S>>) {
  const {getAuthToken, getAuthUser, generatePermissions, ssoAuthenticated = false, children} = props;

  const authToken = useSelector<S, string | undefined>(getAuthToken);
  const user = useSelector<S, Person | undefined>(getAuthUser);
  const userEntity = transformPerson(user)

  const selectPermissions = useMemo(() => {
    return permissionsNameSelector();
  }, []);

  const {permissions} = useGetUserPermissionsQuery(user?.id ? undefined : skipToken, {
    selectFromResult: (result) => ({
      ...result,
      permissions: selectPermissions(result),
      isLoading: result.isLoading,
      error: result.error,
    })
  });

  const selectPermissionABACPolicyAssocs = useMemo(() => {
    return permissionABACPolicyAssocsSelector();
  }, []);

  const {permissionPolicies} = useGetUserPermissionABACPolicyAssocsQuery(user?.id ? undefined : skipToken, {
    selectFromResult: (result) => ({
      ...result,
      permissionPolicies: selectPermissionABACPolicyAssocs(result),
    })
  });

  const abacPoliciesLookup = useMemo<Record<string, any>>(() => {
    const lookup: Record<string, any[]> = {};

    permissionPolicies.forEach((permPolicy) => {
      if (permPolicy.abacPolicyExpression && permPolicy.permissionName) {
        let expr: any;
        try {
          expr = JSON.parse(permPolicy.abacPolicyExpression);
        } catch {
          expr = permPolicy.abacPolicyExpression;
        }

        if (!lookup[permPolicy.permissionName]) {
          lookup[permPolicy.permissionName] = [expr];
        } else {
          lookup[permPolicy.permissionName].push(expr);
        }
      }
    })

    return lookup;
  }, [permissionPolicies]);

  const grantedPermissions = useMemo(() => {
    const generatedPermissions = generatePermissions(userEntity?.type?.code)
    return [...generatedPermissions, ...permissions]
  }, [generatePermissions, userEntity?.type?.code, permissions])

  const subject: ABACContextSubject | undefined = useMemo(() => {
    if (!userEntity) {
      return undefined
    }

    const roles: string[] = []
    if (userEntity.roles) {
      userEntity.roles.forEach(r => roles.push(r.key))
    }

    return {
      userId: userEntity.id,
      userType: userEntity.type.code,
      userCompanyId: userEntity.companyId,
      userCompanyType: userEntity.companyType,
      roles: roles,
      permissions: grantedPermissions,
    }
  }, [userEntity, grantedPermissions]);

  const _hasAccess = useCallback((objOrPerm?: Record<string, unknown> | string, ...rest: string[]) => {
    if (!subject) {
      return false
    }

    const context: ABACContext = {
      subject,
      ...(objOrPerm !== undefined && typeof objOrPerm !== 'string'
        ? {object: objOrPerm as Record<string, never>}
        : {}),
    };

    const perms =
      objOrPerm !== undefined && typeof objOrPerm === 'string'
        ? ([objOrPerm as string] as string[]).concat(rest)
        : rest;

    return hasAccessHelper(
      grantedPermissions,
      abacPoliciesLookup,
      context,
      ...perms)

  }, [grantedPermissions, abacPoliciesLookup, subject])

  const _hasEntityAccess = useCallback((entity: Entity, ...perms: string[]) => {
    const obj = {resource: entity.abacAttributes}
    return _hasAccess(obj, ...perms)
  }, [_hasAccess])

  const value: AuthContextType = {
    authenticated: typeof user !== 'undefined' && typeof authToken !== 'undefined',
    ssoAuthenticated: ssoAuthenticated,
    user: userEntity,
    userId: user?.id,
    companyId: user?.companyId,
    permissions: grantedPermissions,
    hasAccess: _hasAccess,
    hasEntityAccess: _hasEntityAccess,
  };

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}
